import { Parser } from 'expr-eval';
import {
  MarkerIdentity,
  Widget,
} from '../../pdf-signature-customizer/pdf-signature-customizer.model';
import {
  FormInputMarkerIdentity,
  RadioOption,
  TextDropdownOption,
} from '../form-inputs.model';
import {
  FormulaFieldNameMap,
  FormulaGraph,
  FormulaNode,
  FormulaNodeAllowTypes,
  IFormInputDataProvider,
} from './formula.model';
import * as _ from 'lodash';
import {
  FormulaCircularReferenceError,
  FormulaSyntaxError,
} from './formula.errors';

class BaseFormInputDataProvider implements IFormInputDataProvider {
  getFieldName(variant: FormInputMarkerIdentity): string | null {
    return _.get(variant, 'options.fieldName', null);
  }

  getUniqueKey(variant: FormInputMarkerIdentity): string | null {
    return variant.id || null;
  }

  getNumberValue(variant: FormInputMarkerIdentity): number | null {
    return Number(variant.data);
  }
}

class NumberDataProvider extends BaseFormInputDataProvider {}

class FormulaDataProvider extends BaseFormInputDataProvider {}

class TextDropdownDataProvider extends BaseFormInputDataProvider {
  override getNumberValue(
    variant: FormInputMarkerIdentity,
  ): number | null {
    const dropdownVariant =
      variant as FormInputMarkerIdentity<'text_dropdown'>;

    if (!dropdownVariant.options.isForCalculate) {
      return null;
    }

    const selectedLabel = dropdownVariant.data;
    if (!selectedLabel) {
      return null;
    }

    const selectedItem = dropdownVariant.options.items.find(
      (item) => item.label === selectedLabel,
    );
    if (!selectedItem) {
      return null;
    }

    const value = selectedItem.value;
    if (value === null || value === undefined) {
      return null;
    }
    return Number(value);
  }
}

class RadioDataProvider extends BaseFormInputDataProvider {
  override getNumberValue(
    variant: FormInputMarkerIdentity,
  ): number | null {
    const options = variant.options as RadioOption;
    const isChecked = variant.data;
    if (isChecked) {
      return Number(options.fieldValueNumber);
    }
    return null;
  }
}

const _dataProviderMap: Record<
  FormulaNodeAllowTypes | string,
  IFormInputDataProvider | undefined
> = {
  number: new NumberDataProvider(),
  formula: new FormulaDataProvider(),
  text_dropdown: new TextDropdownDataProvider(),
  radio: new RadioDataProvider(),
};

export function getDataProvider(type: string) {
  const provider = _dataProviderMap?.[type];
  if (!provider) {
    throw new Error(`type ${type} is not supported`);
  }
  return provider;
}

export function getKeyValueFromVariant(
  variant: FormInputMarkerIdentity | MarkerIdentity,
  defaultValue = 0,
): {
  uniqueKey: string;
  value: number;
} {
  if (
    ['number', 'formula', 'text_dropdown', 'radio'].includes(
      variant.formInputType || '',
    )
  ) {
    const formInputVariant = variant as FormInputMarkerIdentity;
    const provider = getDataProvider(formInputVariant.formInputType);
    const uniqueKey = provider.getUniqueKey(formInputVariant);
    if (!uniqueKey) {
      throw new Error('The fieldName is undefined.');
    }
    let value = provider.getNumberValue(formInputVariant);
    if (value === null) {
      value = defaultValue;
    }
    return {
      uniqueKey,
      value,
    };
  } else {
    throw new Error('formInputType not supported');
  }
}

const VARIABLE_PATTERN = /\{([^}]+)\}/g;

/**
 * convert from string "({a}+{b})*{c}" to "(1+2)*3"
 */
export function replaceVariables(
  template: string,
  variables: Record<string, number | string | null>,
): string {
  return template.replace(VARIABLE_PATTERN, (_, name) => {
    const value = variables?.[name];
    if (value === undefined || value === null) {
      throw new Error(`Variable "${name}" is not defined.`);
    }
    return value.toString();
  });
}

/**
 * Evaluate a formula with given variables.
 *
 * Replace variables in the formula with the given values, then
 * evaluate the resulting expression.
 *
 * @param parser A parser class for use to calculate number from text
 * @param template String of formula, example "({a}+{b})*{c}"
 * @param variables A dictionary of variable in template, example {a: 1, b: 2, c: 3}
 * @throws {FormulaSyntaxError} if the formula can't be evaluated.
 * @returns The result of the evaluation, or NaN if there is an error.
 */
export function evaluateExpression(
  parser: Parser,
  template: string,
  variables: Record<string, number | string | null>,
): number {
  try {
    const expression = replaceVariables(template, variables);
    const parsed = parser.parse(expression);
    return parsed.evaluate();
  } catch (error) {
    throw new FormulaSyntaxError({
      template,
      variables,
    });
  }
}

/**
 * Convert from string "({a}+{b})*{c}" to ["a", "b", "c"]
 */
export function getFieldNamesFromFormula(formula: string): string[] {
  const matches: string[] = [];
  let match;

  while ((match = VARIABLE_PATTERN.exec(formula)) !== null) {
    matches.push(match[1]);
  }

  return matches;
}

export function checkCanCalculate(
  variant: FormInputMarkerIdentity | MarkerIdentity,
): boolean {
  if (!variant.formInputType) {
    return false;
  }
  const f = variant as FormInputMarkerIdentity;

  if (f.formInputType === 'number' || f.formInputType === 'formula') {
    return true;
  }

  if (
    f.formInputType === 'radio' ||
    f.formInputType === 'text_dropdown'
  ) {
    const op = f.options as RadioOption | TextDropdownOption;
    return !!op.isForCalculate;
  }

  return false;
}

export function generateFormulaFieldNameMap(
  widget: Widget,
): FormulaFieldNameMap {
  const variantMap = new Map<string, Array<FormulaNode['variant']>>();
  widget.positions?.forEach((p) => {
    if (!p.formInputType) {
      return;
    }
    const f = p as FormInputMarkerIdentity;
    if (!checkCanCalculate(f)) {
      return;
    }

    const provider = getDataProvider(f.formInputType);
    const fieldName = provider.getFieldName(f);
    if (!fieldName) {
      return;
    }

    const variantGroup = variantMap.get(fieldName);
    if (!variantGroup) {
      variantMap.set(fieldName, [f]);
    } else {
      // type radio can have multiple variant in the same fieldName
      // but others type will be only one
      variantGroup.push(f);
    }
  });

  return variantMap;
}

/**
 * @throws {FormulaCircularReferenceError} if have circular reference in formula.
 */
export function generateFormulaGraph(
  variantMap: FormulaFieldNameMap,
): FormulaGraph {
  const graph: FormulaGraph = new Map<string, FormulaNode>();
  function _recursiveBuildNode(
    uniqueKey: string,
    variant: FormulaNode['variant'],
    toNode: FormulaNode | null | undefined,
  ): FormulaNode {
    let node = graph.get(uniqueKey);
    if (!node) {
      node = {
        variant,
        toNodes: [],
      };
      graph.set(uniqueKey, node);
    }
    if (toNode) {
      node.toNodes.push(toNode);
      // It can push duplicate node when recursive traversal
      node.toNodes = _.uniqBy(node.toNodes, (n) => {
        const p = getDataProvider(n.variant.formInputType);
        return p.getUniqueKey(n.variant as FormInputMarkerIdentity);
      });
    }

    if (variant.formInputType === 'formula') {
      const f = variant as FormInputMarkerIdentity<'formula'>;
      if (f.options.formula) {
        const nameChildren = getFieldNamesFromFormula(
          f.options.formula,
        );
        nameChildren.forEach((childName) => {
          const childVariantGroup = variantMap.get(childName);
          if (!childVariantGroup) {
            return;
          }
          childVariantGroup.forEach((childVariant) => {
            const p = getDataProvider(childVariant.formInputType);
            const childUniqueKey = p.getUniqueKey(
              childVariant as FormInputMarkerIdentity,
            );
            if (childUniqueKey) {
              _recursiveBuildNode(childUniqueKey, childVariant, node); // recursive build node
            }
          });
        });
      }
    }

    return node;
  }

  // generate graph
  try {
    for (const [fieldName, variantGroup] of variantMap) {
      for (const variant of variantGroup) {
        const p = getDataProvider(variant.formInputType);
        const uniqueKey = p.getUniqueKey(
          variant as FormInputMarkerIdentity,
        );
        if (!uniqueKey) {
          continue;
        }
        graph.set(
          uniqueKey,
          _recursiveBuildNode(uniqueKey, variant, null),
        );
      }
    }
  } catch (error) {
    if (error instanceof RangeError) {
      // call stack size exceed
      throw new FormulaCircularReferenceError();
    }
    throw error;
  }

  return graph;
}

export function recursiveUpdateFormulaValue(
  node: FormulaNode,
  parser: Parser,
  formulaFieldNameMap: FormulaFieldNameMap,
  // formulaGraph: FormulaGraph,
) {
  if (node.variant.formInputType === 'formula') {
    const f = node.variant as FormInputMarkerIdentity<'formula'>;
    if (!f.options.formula) {
      return;
    }

    // generate variables
    const variables: Record<string, number> = {};
    const nameChildren = getFieldNamesFromFormula(f.options.formula);
    nameChildren.forEach((childName) => {
      variables[childName] = 0;

      const variantGroup = formulaFieldNameMap.get(childName);
      if (!variantGroup) {
        return;
      }

      variantGroup.forEach((childVariant) => {
        if (childVariant.formInputType === 'radio') {
          const isChecked = !!childVariant.data;
          if (!isChecked) {
            return;
          }
        }

        try {
          const p = getDataProvider(childVariant.formInputType);
          const value = p.getNumberValue(
            childVariant as FormInputMarkerIdentity,
          );
          if (value !== null) {
            variables[childName] = value;
          }
        } catch {
          return;
        }
      });
    });

    try {
      const result = evaluateExpression(
        parser,
        f.options.formula,
        variables,
      );
      if (Number.isFinite(result)) {
        f.data = result.toFixed(2);
      } else {
        f.data = 'Invalid result';
      }
    } catch {
      f.data = 'Invalid result';
    }
  }

  node.toNodes.forEach((toNode) => {
    recursiveUpdateFormulaValue(toNode, parser, formulaFieldNameMap);
  });
}
